// Create-SMS-campaign form. Campaigns are always created PAUSED (server-side
// default) — the operator goes live from the detail page once templates and
// the send rate are confirmed.
//
// Every campaign is an uploaded recipient list now (v3, MIS-969/970): there is
// no cache/list choice — the form always creates a `list` campaign and the
// operator uploads a recipient CSV from the detail page before going live.
//
// Mirrors pages/MirrorClientNew.tsx: form state, onSubmit → api.createSmsCampaign,
// success card with a "View campaign" button navigating to detail. Templates
// are plain {n}/{p} placeholder strings (one per line in a textarea) — never
// JS functions. {n} → lead first name, {p} → callback DID.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, type SmsCampaignCreateRequest } from "../lib/api";
import SmsPreview from "../components/SmsPreview";
import {
  buttonStyle,
  Card,
  FieldLabel,
  inputStyle,
  PageHeader,
  tokens,
} from "../lib/theme";

// Default {n}/{p} placeholder templates (ported from Loop_landers — see
// PRPs/ai_docs/sms_attribution_reference.md §3d PORT NOTE). The operator can
// edit these before/after creating the campaign.
const DEFAULT_OPENERS = [
  "Hi {n}, thank you for submitting the form.",
  "{n}, thanks for completing your application a moment ago.",
  "{n}, we received your form — thanks for reaching out.",
  "Hi {n}, appreciate you submitting your details.",
];
const DEFAULT_OPENERS_NO_NAME = [
  "Thank you for submitting the form.",
  "Thanks for completing your application a moment ago.",
  "We received your form — thanks for reaching out.",
  "Appreciate you submitting your details.",
];
const DEFAULT_ACTIONS = [
  "A specialist is on the line ready to discuss emergency relief.",
  "We have a specialist holding the line to walk through emergency relief options.",
  "Your emergency relief specialist is on the line waiting.",
  "A relief specialist is ready right now to review your emergency options.",
];
const DEFAULT_CTAS = [
  "Call {p} now.",
  "Dial {p} to connect.",
  "Tap to call {p}.",
  "Phone: {p}.",
];

const DEFAULT_CALLBACK_DID = "1-888-533-1148";
const DEFAULT_MSGS_PER_MIN = 60;
// Opt-out suffix appended to every body by buildSmsBody. Editable plain text
// (no {n}/{p} placeholders) — mirrors the admin-api DEFAULT_SMS_CAMPAIGN.footer.
const DEFAULT_FOOTER = "Reply STOP to stop getting messages. Thank you.";

interface FormState {
  campaign_name: string;
  vertical: string;
  callback_did: string;
  msgs_per_min: number;
  openers: string;
  openers_no_name: string;
  actions: string;
  ctas: string;
  footer: string;
  skip_resend_guard: boolean;
}

const DEFAULT_FORM: FormState = {
  campaign_name: "",
  vertical: "",
  callback_did: DEFAULT_CALLBACK_DID,
  msgs_per_min: DEFAULT_MSGS_PER_MIN,
  openers: DEFAULT_OPENERS.join("\n"),
  openers_no_name: DEFAULT_OPENERS_NO_NAME.join("\n"),
  actions: DEFAULT_ACTIONS.join("\n"),
  ctas: DEFAULT_CTAS.join("\n"),
  footer: DEFAULT_FOOTER,
  skip_resend_guard: false,
};

// Split a textarea into trimmed, non-empty lines (one template per line).
function toLines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export default function SmsCampaignNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const rateWarning =
    form.msgs_per_min <= 0 || form.msgs_per_min > 600
      ? "Msgs/min should be between 1 and 600."
      : null;

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: SmsCampaignCreateRequest = {
        campaign_name: form.campaign_name.trim(),
        vertical: form.vertical.trim(),
        // Every campaign is an uploaded recipient list (v3) — no cache option.
        lead_source: "list",
        callback_did: form.callback_did.trim(),
        msgs_per_min: form.msgs_per_min,
        openers: toLines(form.openers),
        openers_no_name: toLines(form.openers_no_name),
        actions: toLines(form.actions),
        ctas: toLines(form.ctas),
        footer: form.footer.trim() || DEFAULT_FOOTER,
        skip_resend_guard: form.skip_resend_guard,
      };
      const res = await api.createSmsCampaign(body);
      // Two-step flow: creating the campaign is step 1; uploading its recipient
      // CSV is step 2. Go STRAIGHT to the campaign detail page's Recipient-list
      // card (#recipients) instead of a success card / the campaign list, so the
      // operator can upload immediately without navigating back in.
      navigate(
        `/sms/campaigns/${encodeURIComponent(res.campaign_id)}#recipients`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="New SMS campaign"
        subtitle="Upload-only — created paused; upload a recipient list and go live from the campaign page"
      />
      <Card>
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel htmlFor="new-sms-name">Campaign name</FieldLabel>
              <input
                id="new-sms-name"
                required
                value={form.campaign_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, campaign_name: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-sms-vertical">Vertical</FieldLabel>
              <input
                id="new-sms-vertical"
                required
                placeholder="debt"
                value={form.vertical}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vertical: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-sms-did">Callback DID</FieldLabel>
              <input
                id="new-sms-did"
                value={form.callback_did}
                onChange={(e) =>
                  setForm((f) => ({ ...f, callback_did: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-sms-rate" hint="1–600 (sends per minute)">
                Msgs/min
              </FieldLabel>
              <input
                id="new-sms-rate"
                required
                type="number"
                min="1"
                max="600"
                value={form.msgs_per_min}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    msgs_per_min: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: tokens.space.lg,
              padding: tokens.space.md,
              background: "rgba(0,212,170,0.06)",
              border: "1px solid rgba(0,212,170,0.30)",
              color: tokens.color.fg1,
              borderRadius: tokens.radius.sm,
              fontSize: tokens.font.size.sm,
            }}
          >
            Campaigns text only the recipients you upload. After creating, open
            the campaign and upload a recipient list (CSV) from the{" "}
            <strong style={{ color: tokens.color.fg0 }}>Recipient list</strong>{" "}
            card before going live.
          </div>

          <div style={{ marginTop: tokens.space.lg }}>
            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                margin: `0 0 ${tokens.space.md}px`,
              }}
            >
              Templates: one variant per line. Use{" "}
              <code style={{ fontFamily: tokens.font.mono }}>{"{n}"}</code> for
              the lead&apos;s first name and{" "}
              <code style={{ fontFamily: tokens.font.mono }}>{"{p}"}</code> for
              the callback DID.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: tokens.space.lg,
              }}
            >
              <TemplateField
                id="new-sms-openers"
                label="Openers (with name)"
                value={form.openers}
                onChange={(v) => setForm((f) => ({ ...f, openers: v }))}
              />
              <TemplateField
                id="new-sms-openers-no-name"
                label="Openers (no name)"
                value={form.openers_no_name}
                onChange={(v) =>
                  setForm((f) => ({ ...f, openers_no_name: v }))
                }
              />
              <TemplateField
                id="new-sms-actions"
                label="Actions"
                value={form.actions}
                onChange={(v) => setForm((f) => ({ ...f, actions: v }))}
              />
              <TemplateField
                id="new-sms-ctas"
                label="CTAs"
                value={form.ctas}
                onChange={(v) => setForm((f) => ({ ...f, ctas: v }))}
              />
            </div>
          </div>

          <div style={{ marginTop: tokens.space.lg }}>
            <FieldLabel
              htmlFor="new-sms-footer"
              hint="appended to every message (plain text — no {n}/{p})"
            >
              Footer / opt-out suffix
            </FieldLabel>
            <input
              id="new-sms-footer"
              value={form.footer}
              maxLength={160}
              onChange={(e) =>
                setForm((f) => ({ ...f, footer: e.target.value }))
              }
              style={inputStyle}
            />
          </div>

          {/* Skip resend guard — bypass the 14-day cross-campaign no-re-text
              rule for this campaign. Default OFF; clearly flagged as overriding
              double-text protection. */}
          <div style={{ marginTop: tokens.space.lg }}>
            <label
              htmlFor="new-sms-skip-guard"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: tokens.space.sm,
                cursor: "pointer",
              }}
            >
              <input
                id="new-sms-skip-guard"
                type="checkbox"
                checked={form.skip_resend_guard}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    skip_resend_guard: e.target.checked,
                  }))
                }
                style={{ marginTop: 3 }}
              />
              <span>
                <span
                  style={{
                    color: tokens.color.fg0,
                    fontSize: tokens.font.size.base,
                    fontWeight: 600,
                  }}
                >
                  Skip resend guard
                </span>
                <span
                  style={{
                    display: "block",
                    color: form.skip_resend_guard
                      ? tokens.color.warning
                      : tokens.color.fg1,
                    fontSize: tokens.font.size.sm,
                    marginTop: 2,
                  }}
                >
                  Text recipients even if they were messaged in the last 14 days
                  (by any campaign). Overrides double-text protection — use only
                  for deliberate re-sends or testing. Quiet hours still apply.
                </span>
              </span>
            </label>
          </div>

          {/* Live preview of a real composed message from the current copy. */}
          <SmsPreview
            openers={toLines(form.openers)}
            openers_no_name={toLines(form.openers_no_name)}
            actions={toLines(form.actions)}
            ctas={toLines(form.ctas)}
            callback_did={form.callback_did}
            footer={form.footer}
          />

          {rateWarning && (
            <div
              style={{
                marginTop: tokens.space.lg,
                padding: tokens.space.md,
                background: "rgba(212,168,83,0.12)",
                border: "1px solid rgba(212,168,83,0.40)",
                color: tokens.color.warning,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.sm,
              }}
            >
              {rateWarning}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                marginTop: tokens.space.lg,
                padding: tokens.space.md,
                background: tokens.color.dangerBg,
                border: `1px solid ${tokens.color.dangerBorder}`,
                color: tokens.color.danger,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              marginTop: tokens.space.lg,
              display: "flex",
              gap: tokens.space.sm,
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={buttonStyle("primary", submitting)}
            >
              {submitting ? "Creating…" : "Next →"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/sms/campaigns")}
              disabled={submitting}
              style={buttonStyle("ghost", submitting)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function TemplateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <textarea
        id={id}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          resize: "vertical",
          fontFamily: tokens.font.mono,
          fontSize: tokens.font.size.sm,
          lineHeight: 1.5,
        }}
      />
    </div>
  );
}
